import { WXCC_ADDON_LABELS } from "constant/wxcc-addons";
import { Sku, WxccAddonSku } from "types/summary";

export interface AddOnItem {
  id: string;
  label: string;
  value: number;
}

export interface AddOnGroup {
  category: typeof WXCC_ADDON_LABELS.supported | typeof WXCC_ADDON_LABELS.unsupported;
  color: string;
  backgroundColor: string;
  items: AddOnItem[];
}

export type AddOnTotals = Partial<Record<AddOnGroup["category"], number>>;

export interface AddOnSkus {
  supported?: WxccAddonSku[];
  unsupported?: WxccAddonSku[];
}

export const buildAddOnGroups = (skus: AddOnSkus = {}, arrToMrrDivisor = 1): AddOnGroup[] => {
  const mapItems = (items: WxccAddonSku[] = []): AddOnItem[] => {
    const occurrences = new Map<string, number>();

    return (Array.isArray(items) ? items : []).map((item) => {
      const skuKey = JSON.stringify([item.accountId, item.skuId]);
      const occurrence = occurrences.get(skuKey) ?? 0;
      occurrences.set(skuKey, occurrence + 1);

      return {
        id: JSON.stringify([item.accountId, item.skuId, occurrence]),
        label: item.skuLabel || item.skuId,
        value: (Number(item.arrAdjUsd ?? 0) || 0) / arrToMrrDivisor,
      };
    });
  };

  return [
    {
      category: WXCC_ADDON_LABELS.supported,
      color: "#1B6E3C",
      backgroundColor: "#E8F5EC",
      items: mapItems(skus.supported),
    },
    {
      category: WXCC_ADDON_LABELS.unsupported,
      color: "#9B3A2C",
      backgroundColor: "#FCEDEB",
      items: mapItems(skus.unsupported),
    },
  ];
};

export const getGroupTotal = (items: AddOnItem[]) =>
  items.reduce((total, item) => total + item.value, 0);

// The account API supplies SKU classification; amounts come from the selected subscription.
export const buildSubscriptionAddOnSkus = (
  skuDetails: Sku[] = [],
  classification: AddOnSkus = {}
): AddOnSkus => {
  const normalizeId = (id: string) => id.trim().toUpperCase();
  const supportedIds = new Map(
    (classification.supported ?? []).map((sku) => [normalizeId(sku.skuId), sku])
  );
  const unsupportedIds = new Map(
    (classification.unsupported ?? []).map((sku) => [normalizeId(sku.skuId), sku])
  );
  const supported = new Map<string, WxccAddonSku>();
  const unsupported = new Map<string, WxccAddonSku>();

  const visit = (skus: Sku[]) => {
    if (!Array.isArray(skus)) return;

    for (const sku of skus) {
      // Bundle parents sum their children, so only count leaf rows.
      if (sku.childDetails?.length) {
        visit(sku.childDetails);
        continue;
      }
      if (sku.recurringFlag?.toUpperCase().startsWith("N")) continue;
      const ids = [sku.attributeSkuId, sku.skuId]
        .filter((id): id is string => Boolean(id))
        .map(normalizeId);
      const id = ids.find((id) => supportedIds.has(id) || unsupportedIds.has(id));
      if (!id) continue;
      const metadata = supportedIds.get(id) ?? unsupportedIds.get(id)!;
      const items = supportedIds.has(id) ? supported : unsupported;
      const amount = Number(sku.invoiceArr ?? (sku.invoiceMrr ?? 0) * 12);
      if (!Number.isFinite(amount)) continue;
      const existing = items.get(id);
      items.set(id, {
        ...metadata,
        accountId: sku.accountId || metadata.accountId,
        skuLabel: metadata.skuLabel || sku.skuDescription || metadata.skuId,
        arrAdjUsd: (existing?.arrAdjUsd ?? 0) + amount,
      });
    }
  };
  visit(skuDetails);
  return {
    supported: Array.from(supported.values()),
    unsupported: Array.from(unsupported.values()),
  };
};
